import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { ModalShell } from "@/components/ModalShell";
import MobileBrandHeader from "@/components/MobileBrandHeader";
import {
  ChevronRight, LogOut, Moon, Sun, Globe, BellRing, Shield, HelpCircle,
  Leaf, Plus, Star, Home as HomeIcon, Trash2, Trees, TrendingUp, Sparkles, Phone, Mail, Info
} from "lucide-react";

const ENEC_QUICK = [
  { name: "ENEC HQ", address: "ENEC HQ, Corniche, Abu Dhabi", lat: 24.4539, lng: 54.3773 },
  { name: "Barakah NPP", address: "Barakah Nuclear Power Plant, Al Dhafra", lat: 23.9686, lng: 52.1925 },
  { name: "Khalifa City", address: "Khalifa City, Abu Dhabi", lat: 24.422, lng: 54.578 },
  { name: "Al Raha Beach", address: "Al Raha Beach, Abu Dhabi", lat: 24.4275, lng: 54.6097 },
  { name: "Yas Island", address: "Yas Island, Abu Dhabi", lat: 24.4672, lng: 54.6031 },
];

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { dark, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [places, setPlaces] = useState([]);
  const [sustain, setSustain] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [newPlace, setNewPlace] = useState({ name: "", lat: 0, lng: 0, address: "", is_favorite: true, is_home: false });

  const load = async () => {
    const p = await api.get("/me/saved-places");
    setPlaces(p.data || []);
    const s = await api.get("/me/sustainability");
    setSustain(s.data);
  };

  useEffect(() => { load(); }, []);

  async function addPlace() {
    if (!newPlace.name || !newPlace.address || !newPlace.lat) return;
    try {
      await api.post("/me/saved-places", newPlace);
      toast.success(t("save"));
      setShowAdd(false);
      setNewPlace({ name: "", lat: 0, lng: 0, address: "", is_favorite: true, is_home: false });
      load(); refresh();
    } catch (e) { toast.error(fmtErr(e)); }
  }

  async function delPlace(id) {
    await api.delete(`/me/saved-places/${id}`);
    toast.success("Removed");
    load();
  }

  async function setAsHome(p) {
    try {
      await api.patch(`/me/saved-places/${p.id}`, { is_home: true, lat: p.lat, lng: p.lng, address: p.address });
      toast.success("Home updated");
      load(); refresh();
    } catch (e) { toast.error(fmtErr(e)); }
  }

  async function toggleFav(p) {
    try {
      await api.patch(`/me/saved-places/${p.id}`, { is_favorite: !p.is_favorite });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <MobileBrandHeader testId="profile-brand-header" />

      <div className="bg-white pt-3 pb-6 px-5 border-b border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold text-3xl">
            {user?.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-2xl text-slate-900" data-testid="profile-name">{user?.name}</h1>
            <p className="text-sm text-slate-500 font-ar">{user?.name_ar}</p>
            <p className="text-xs text-slate-400 font-mono mt-1">{user?.enec_id} · {user?.department}</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-6 pb-32">
        {/* CO2 saved card */}
        {sustain && (
          <div className="rounded-3xl p-5 relative overflow-hidden text-white" data-testid="co2-card" style={{
            background: "linear-gradient(135deg, #059669 0%, #10b981 60%, #34d399 100%)",
          }}>
            <div className="absolute -right-16 -top-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
            <div className="flex items-center justify-between relative">
              <Leaf size={28} />
              <span className="text-[10px] uppercase tracking-widest font-bold bg-white/20 px-2.5 py-1 rounded-full">{sustain.tier} {t("tier")}</span>
            </div>
            <p className="text-[11px] uppercase tracking-widest text-white/80 font-bold mt-3">{t("co2_month")}</p>
            <div className="flex items-end gap-2 mt-1">
              <p className="font-display font-bold text-5xl leading-none">{sustain.co2_kg_month}</p>
              <p className="text-lg font-semibold text-white/80 mb-1">kg</p>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 relative">
              <Mini icon={Trees} label={t("trees_equiv")} value={sustain.trees_equivalent_month} />
              <Mini icon={TrendingUp} label={t("rides")} value={sustain.rides_month} />
              <Mini icon={Sparkles} label={t("lifetime")} value={`${sustain.co2_kg_lifetime} kg`} />
            </div>
          </div>
        )}

        {/* Saved places */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{t("saved_places")}</p>
            <button onClick={() => setShowAdd(true)} data-testid="add-place-btn" className="text-xs font-bold text-primary uppercase tracking-wide flex items-center gap-1 active:scale-95 transition">
              <Plus size={12} /> {t("add")}
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {places.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-400 text-sm">{t("no_saved_places")}</div>
            ) : places.map((p, idx) => (
              <div key={p.id} data-testid={`place-${p.id}`} className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-slate-100" : ""}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.is_home ? "bg-amber-100 text-amber-600" : "bg-primary-50 text-primary"}`}>
                  {p.is_home ? <HomeIcon size={18} /> : <Star size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{p.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{p.address}</p>
                </div>
                {!p.is_home && (
                  <button
                    onClick={() => toggleFav(p)}
                    data-testid={`fav-toggle-${p.id}`}
                    className="w-9 h-9 rounded-lg hover:bg-amber-50 flex items-center justify-center active:scale-90 transition"
                    aria-label="Toggle favorite"
                  >
                    <Star size={18} className={p.is_favorite ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                  </button>
                )}
                {!p.is_home && (
                  <button onClick={() => setAsHome(p)} data-testid={`set-home-${p.id}`} className="text-[10px] font-bold text-primary uppercase tracking-wide px-2 py-1 rounded-lg hover:bg-primary-50 active:scale-95">
                    {t("set_home")}
                  </button>
                )}
                <button onClick={() => delPlace(p.id)} data-testid={`del-place-${p.id}`} className="text-slate-300 hover:text-rose-500 active:scale-90 transition">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Preferences */}
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2 px-2">{t("preferences")}</p>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <RowButton icon={Globe} label={t("language")} value={lang === "ar" ? t("language_ar") : t("language_en")} onClick={() => setShowLang(true)} testId="p-lang" />
            <RowToggle icon={dark ? Moon : Sun} label={t("dark_mode")} on={dark} onChange={toggleTheme} testId="p-theme" />
            <RowButton icon={BellRing} label={t("notifications")} value="Push + In-app" onClick={() => setShowNotif(true)} testId="p-notif" />
            <RowToggle icon={Sparkles} label="Biometric login" on={localStorage.getItem("teslam_bio") === "1"} onChange={() => {
              const next = localStorage.getItem("teslam_bio") !== "1";
              localStorage.setItem("teslam_bio", next ? "1" : "0");
              toast.success(next ? "Biometric enabled — sign in once more, then unlock with Face/Touch ID on the native app." : "Biometric disabled");
              load();
            }} testId="p-biometric" />
          </div>
        </div>

        {/* Support */}
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2 px-2">{t("support")}</p>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <RowButton icon={Shield} label={t("about_teslam")} onClick={() => setShowAbout(true)} testId="p-about" />
            <RowButton icon={HelpCircle} label={t("help_emergencies")} onClick={() => setShowHelp(true)} testId="p-help" />
          </div>
        </div>

        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/login", { replace: true }); }}
          className="w-full h-12 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition"
        >
          <LogOut size={18} /> {t("sign_out")}
        </button>

        <p className="text-center text-xs text-slate-400 font-mono">TESLAM · v1.1.0 · ENEC</p>
      </div>

      {/* Modals */}
      <ModalShell open={showAdd} onClose={() => setShowAdd(false)} title={t("add_place")} testId="add-place-modal">
        <div className="grid grid-cols-2 gap-2 mb-3">
          {ENEC_QUICK.map((q) => (
            <button
              key={q.name}
              data-testid={`quick-place-${q.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setNewPlace({ ...newPlace, name: q.name, address: q.address, lat: q.lat, lng: q.lng })}
              className={`p-2.5 rounded-xl border text-left text-xs font-medium ${newPlace.address === q.address ? "border-primary bg-primary-50" : "border-slate-200 bg-white hover:border-slate-300"} active:scale-95 transition`}
            >
              <span className="block text-slate-900 font-semibold">{q.name}</span>
              <span className="block text-[10px] text-slate-400 truncate">{q.address}</span>
            </button>
          ))}
        </div>
        <input
          data-testid="place-name-input"
          placeholder="Name (e.g. Home, Mom's place)"
          value={newPlace.name}
          onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
          className="w-full h-11 px-3 mb-2 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-primary"
        />
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="place-is-home"
            checked={newPlace.is_home}
            onChange={(e) => setNewPlace({ ...newPlace, is_home: e.target.checked })}
            className="w-4 h-4 accent-primary"
          />
          Set as my home location
        </label>
        <button
          data-testid="save-place"
          onClick={addPlace}
          disabled={!newPlace.name || !newPlace.address}
          className="w-full h-12 rounded-xl bg-primary text-white font-semibold disabled:opacity-50 active:scale-[0.98] transition"
        >
          {t("save")}
        </button>
      </ModalShell>

      <ModalShell open={showLang} onClose={() => setShowLang(false)} title={t("language")} testId="lang-modal">
        <div className="space-y-2">
          {[{ code: "en", label: t("language_en"), hint: "Left-to-right" }, { code: "ar", label: t("language_ar"), hint: "اتجاه يميني" }].map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => { setLang(l.code); setShowLang(false); toast.success(l.label); }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition active:scale-[0.98] ${lang === l.code ? "border-primary bg-primary-50/50" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="text-left">
                <p className="font-display font-bold text-slate-900">{l.label}</p>
                <p className="text-xs text-slate-500">{l.hint}</p>
              </div>
              {lang === l.code && <span className="text-primary font-bold">✓</span>}
            </button>
          ))}
        </div>
      </ModalShell>

      <ModalShell open={showNotif} onClose={() => setShowNotif(false)} title={t("notifications")} testId="notif-prefs-modal">
        <div className="space-y-3">
          {[
            { k: "push", label: "Push notifications", desc: "Driver assigned, arrival, completion" },
            { k: "email", label: "Email receipts", desc: "Trip summaries and ESG monthly digest" },
            { k: "sos", label: "SOS quick-call", desc: "Show ENEC security button on tracking" },
          ].map((opt) => (
            <div key={opt.k} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-slate-50">
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">{opt.label}</p>
                <p className="text-xs text-slate-500">{opt.desc}</p>
              </div>
              <ToggleSwitch defaultChecked />
            </div>
          ))}
        </div>
      </ModalShell>

      <ModalShell open={showAbout} onClose={() => setShowAbout(false)} title={t("about_teslam")} testId="about-modal">
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">TESLAM (تسلم) is the unified employee transport platform for Emirates Nuclear Energy Corporation (ENEC). Built to make every commute between Abu Dhabi and Barakah safer, smarter, and greener.</p>
          <div className="rounded-xl bg-slate-50 p-3 space-y-2">
            <Detail icon={Info} label="Version" value="1.1.0" />
            <Detail icon={Mail} label="Operator" value="ENEC · Transport Operations" />
            <Detail icon={Shield} label="Data residency" value="UAE — Abu Dhabi" />
          </div>
        </div>
      </ModalShell>

      <ModalShell open={showHelp} onClose={() => setShowHelp(false)} title={t("help_emergencies")} testId="help-modal">
        <div className="space-y-3">
          <a href="tel:999" data-testid="emergency-999" className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 hover:bg-rose-100 active:scale-[0.98] transition">
            <div className="w-10 h-10 rounded-full bg-rose-500 text-white flex items-center justify-center font-display font-bold">!</div>
            <div className="flex-1">
              <p className="font-display font-bold text-slate-900">UAE Emergency · 999</p>
              <p className="text-xs text-slate-500">Police / Ambulance / Fire</p>
            </div>
            <Phone size={18} className="text-rose-600" />
          </a>
          <a href="tel:+97128367000" data-testid="enec-security" className="flex items-center gap-3 p-3 rounded-xl bg-primary-50 hover:bg-primary-100 active:scale-[0.98] transition">
            <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-display font-bold">E</div>
            <div className="flex-1">
              <p className="font-display font-bold text-slate-900">ENEC Security Hotline</p>
              <p className="text-xs text-slate-500">+971 2 836 7000</p>
            </div>
            <Phone size={18} className="text-primary" />
          </a>
          <a href="mailto:transport@enec.ae" data-testid="transport-team" className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 active:scale-[0.98] transition">
            <div className="w-10 h-10 rounded-full bg-slate-700 text-white flex items-center justify-center font-display font-bold">T</div>
            <div className="flex-1">
              <p className="font-display font-bold text-slate-900">Transport Operations</p>
              <p className="text-xs text-slate-500">transport@enec.ae</p>
            </div>
            <Mail size={18} className="text-slate-600" />
          </a>
        </div>
      </ModalShell>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/15 backdrop-blur rounded-xl p-2.5">
      <Icon size={14} className="text-white/80" />
      <p className="font-display font-bold text-lg leading-tight mt-1">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-white/70 font-bold">{label}</p>
    </div>
  );
}

function RowButton({ icon: Icon, label, value, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition active:scale-[0.99] text-left border-b last:border-b-0 border-slate-100">
      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
        <Icon size={18} />
      </div>
      <span className="flex-1 font-medium text-slate-800 text-sm">{label}</span>
      {value && <span className="text-xs text-slate-500">{value}</span>}
      <ChevronRight size={16} className="text-slate-300" />
    </button>
  );
}

function RowToggle({ icon: Icon, label, on, onChange, testId }) {
  return (
    <button data-testid={testId} onClick={onChange} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition active:scale-[0.99] text-left border-b last:border-b-0 border-slate-100">
      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
        <Icon size={18} />
      </div>
      <span className="flex-1 font-medium text-slate-800 text-sm">{label}</span>
      <ToggleSwitch checked={on} />
    </button>
  );
}

function ToggleSwitch({ checked, defaultChecked }) {
  const c = checked !== undefined ? checked : defaultChecked;
  return (
    <span className={`relative inline-block w-11 h-6 rounded-full transition-colors ${c ? "bg-primary" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 ${c ? "left-5" : "left-0.5"} w-5 h-5 rounded-full bg-white shadow transition-all`} />
    </span>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon size={14} className="text-primary" />
      <span className="text-slate-600 flex-1">{label}</span>
      <span className="font-semibold text-slate-900 font-mono text-xs">{value}</span>
    </div>
  );
}
